import { useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import DownloadIcon from '@mui/icons-material/Download'
import ContentFrame, { PageHeading } from '../components/assessment/ContentFrame'
import { downloadCertificatePdf } from '../components/pdf/certificateDownload'
import { errorMessage, getCertificate, getEnrolment, isOk, issueCertificate } from '../helpers/trainingApi'

const formatDate = (value) => (value ? moment(value).format('D MMMM YYYY') : '')

const Detail = ({ label, children }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography component="dt" sx={{ width: { xs: '100%', sm: 200 }, color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography component="dd" sx={{ m: 0, fontWeight: 700, overflowWrap: 'anywhere', flex: 1, minWidth: 0 }}>
      {children}
    </Typography>
  </Box>
)

// The completion screen.
const CourseCertificate = () => {
  const { enrolmentId } = useParams()
  const coursePath = `/training/course/${enrolmentId}`

  // 'loading' | 'error' | 'notPassed' | 'feedbackNeeded' | 'ready'
  const [step, setStep] = useState('loading')
  const [error, setError] = useState('')
  const [certificate, setCertificate] = useState(null)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState('')
  const headingRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    const fail = (res, fallback) => {
      setError(errorMessage(res, fallback))
      setStep('error')
    }
    const load = async () => {
      setStep('loading')
      const enrolmentRes = await getEnrolment(enrolmentId)
      if (cancelled) return
      if (!isOk(enrolmentRes)) return fail(enrolmentRes, 'We could not load this course. Please try again.')
      const { enrolment, feedbackSubmitted } = enrolmentRes.data

      let certificateId = enrolment?.certificate
      if (!certificateId) {
        if (!enrolment?.passed) return setStep('notPassed')
        if (!feedbackSubmitted) return setStep('feedbackNeeded')
        const issued = await issueCertificate(enrolmentId)
        if (cancelled) return
        if (!isOk(issued)) return fail(issued, 'We could not issue your certificate. Please try again.')
        certificateId = issued.data.certificateId
      }

      const certificateRes = await getCertificate(certificateId)
      if (cancelled) return
      if (!isOk(certificateRes)) return fail(certificateRes, 'We could not load your certificate. Please try again.')
      setCertificate(certificateRes.data)
      return setStep('ready')
    }
    load()
    return () => {
      cancelled = true
    }
  }, [enrolmentId])

  useEffect(() => {
    if (step !== 'loading') headingRef.current?.focus()
  }, [step])

  const download = async () => {
    setDownloading(true)
    setDownloadError('')
    try {
      await downloadCertificatePdf(certificate)
    } catch (e) {
      console.error(e)
      setDownloadError('We could not create the PDF. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  if (step === 'loading') {
    return (
      <ContentFrame>
        <Box role="status">
          <Typography sx={{ mb: 1 }}>Getting your certificate…</Typography>
          <LinearProgress />
        </Box>
      </ContentFrame>
    )
  }

  if (step !== 'ready') {
    return (
      <ContentFrame>
        <PageHeading ref={headingRef}>Certificate</PageHeading>
        {step === 'error' && (
          <>
            <Alert severity="error" role="alert" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <RouterLink to="/mandatory-training">Back to my training</RouterLink>
          </>
        )}
        {step === 'notPassed' && (
          <>
            <Typography sx={{ mb: 2 }}>
              A certificate is only issued after passing the assessment. You have not passed it yet.
            </Typography>
            <Button component={RouterLink} to={coursePath} variant="contained">
              Back to the course
            </Button>
          </>
        )}
        {step === 'feedbackNeeded' && (
          <>
            <Typography sx={{ mb: 2 }}>
              You have passed the assessment. Complete one short feedback form to receive your certificate.
            </Typography>
            <Button component={RouterLink} to={`${coursePath}/feedback`} variant="contained">
              Go to the feedback form
            </Button>
          </>
        )}
      </ContentFrame>
    )
  }

  const { snapshot } = certificate
  const revoked = !!certificate.revokedAt

  return (
    <ContentFrame>
      {certificate.isSample && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Sample certificate.</strong> This is a sample produced in reviewer mode. It is not a valid certificate
          and is not evidence of training.
        </Alert>
      )}
      {revoked && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Withdrawn.</strong> This certificate was withdrawn on {formatDate(certificate.revokedAt)} and is no
          longer valid.
        </Alert>
      )}

      <PageHeading ref={headingRef}>You have completed {snapshot.courseTitle}</PageHeading>
      {snapshot.pathwayTitle && <Typography sx={{ mb: 2 }}>{snapshot.pathwayTitle}</Typography>}

      <Box component="dl" sx={{ m: 0, mb: 3 }}>
        <Detail label="Score">
          {snapshot.score} out of {snapshot.total} ({snapshot.percentage}%)
        </Detail>
        <Detail label="Completed on">{formatDate(snapshot.completedOn)}</Detail>
        <Detail label="Valid until">{formatDate(snapshot.expiresOn) || 'No expiry date'}</Detail>
        <Detail label="Verification ID">{certificate.verificationId}</Detail>
      </Box>

      {snapshot.practicalOutcomes?.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          This certificate covers the e-learning (knowledge) part of this course. Your organisation will arrange and
          record the practical sign-off.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={download} disabled={downloading}>
          {downloading
            ? 'Creating your PDF…'
            : certificate.isSample
              ? 'Download sample certificate (PDF)'
              : 'Download certificate (PDF)'}
        </Button>
        <Button component={RouterLink} to="/mandatory-training" variant="outlined">
          Back to my training
        </Button>
      </Stack>
      <Box aria-live="assertive" sx={{ mt: 2 }}>
        {downloadError && <Alert severity="error">{downloadError}</Alert>}
      </Box>
    </ContentFrame>
  )
}

export default CourseCertificate
